/**
 * useDigitalCheckout — hook con toda la lógica de estado + handlers del
 * DigitalCheckoutModal (SPEC-244 · §Sprint5 partición VERBATIM de
 * `frontend/src/components/market/checkout-modal.tsx` 2.637 L).
 *
 * Extrae VERBATIM toda la lógica de:
 *   - State declarations (step, show2fa, submitting, submitError)
 *     [2FA state removed per Founder directive 2026-11: checkout no
 *     longer requires 2FA]
 *   - INV-SEC-03 idempotencyKeyRef (safeUUID, reused within a modal
 *     session — fresh UUID on each open)
 *   - grandTotal (parseFloat of `product.priceCredits` — digital variant
 *     has no variations, no quantity, no shipping; INV-MKT-14)
 *   - useEffect reset on `open` change (digital starts at `'summary'` —
 *     1-step flow per SPEC §6 Task C)
 *   - callCreate (POST /api/market/orders with productType='digital';
 *     SPEC-157 SINGLE_PURCHASE_ALREADY_BOUGHT intercept)
 *   - handleOpenChange (anti-P-043 — block close during submit)
 *
 * Transformaciones documentadas (las ÚNICAS):
 *   - T-HOOK-EXTRACTION: la lógica se mueve del cuerpo del componente a un
 *     hook personalizado `useDigitalCheckout({ open, onClose, product })`
 *     que retorna todo el state + handlers.
 *   - T-IMPORTS: recalcula imports (solo lo que el hook usa).
 *
 * §6 Anti-Invención: NO business logic. Anti-P-371: cero cambios.
 * Anti-P-041: 0 `as any` (the `as Route` cast is in the view, not here).
 * Anti-P-042: 0 `console.*`. Anti-P-043: submitting state blocks close.
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { api, ApiClientError } from '@/lib/api-client';
import { safeUUID } from '@/lib/utils';
import type { Route } from 'next';
import type {
  PublicProductResponse,
  OrderCreateRequestDigital,
  OrderResponse,
} from '@profileia/shared';
import { mapCreateError } from '../helpers';
import type { Step } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseDigitalCheckoutArgs {
  open: boolean;
  onClose: () => void;
  product: PublicProductResponse;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useDigitalCheckout({ open, onClose, product }: UseDigitalCheckoutArgs) {
  const router = useRouter();
  const [step, setStep] = useState<Step>('summary');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // INV-SEC-03: idempotency key — same pattern as the physical variant.
  const idempotencyKeyRef = useRef<string>(safeUUID());

  const grandTotal = parseFloat(product.priceCredits);

  // Reset state every time the modal opens.
  useEffect(() => {
    if (open) {
      // Digital variant starts at 'summary' (1-step — no 'details' step
      // per SPEC §6 Task C: "1-step (summary + 2FA)").
      setStep('summary');
      setSubmitting(false);
      setSubmitError(null);
      idempotencyKeyRef.current = safeUUID();
    }
  }, [open]);

  // ─── Submit handler ──────────────────────────────────────────────────────

  const callCreate = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: OrderCreateRequestDigital = {
        productType: 'digital',
        productId: product.productId,
      };

      const result: OrderResponse = await api.market.orders.create(payload, {
        idempotencyKey: idempotencyKeyRef.current,
      });

      toast.success('¡Compra completada!', {
        description: `Tu orden ${result.orderNumber} está lista para descarga.`,
      });
      // Redirect to the order detail page where the download button is
      // shown (Task C2 — `app/market/ordenes/[orderNumber]/page.tsx`
      // renders the download CTA when `productType='digital'`).
      router.push(`/market/ordenes/${result.orderNumber}` as Route);
      onClose();
    } catch (err) {
      // SPEC-157: intercept 409 SINGLE_PURCHASE_ALREADY_BOUGHT — digital
      // recurrente (ej: membresía VIP digital anual) — el comprador ya es
      // cliente. Info toast + close modal.
      if (err instanceof ApiClientError && err.code === 'SINGLE_PURCHASE_ALREADY_BOUGHT') {
        toast.info('Ya eres cliente de este servicio', {
          description: 'Contacta directamente al vendedor para gestionar tus pagos recurrentes.',
          duration: 6000,
        });
        onClose();
        return;
      }
      if (err instanceof ApiClientError) {
        const mapped = mapCreateError(err);
        if (mapped !== null) {
          setSubmitError(mapped);
          if (err.code === 'IDEMPOTENCY_CONFLICT') {
            idempotencyKeyRef.current = safeUUID();
          }
        } else {
          setSubmitError(err.message ?? 'Error al procesar la compra.');
        }
      } else if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      } else {
        setSubmitError('Error al procesar la compra. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (o: boolean) => {
    if (!o && submitting) return;
    if (!o) onClose();
  };

  return {
    // state
    step,
    setStep,
    submitting,
    submitError,
    // derived
    grandTotal,
    // handlers
    callCreate,
    handleOpenChange,
  };
}
